import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Circle, Clock, MapPin, Shield, Loader2, XCircle } from 'lucide-react';
import { BRAND } from './kit/tokens';
import { formatDate } from './kit/time';
import { STAGES, FAIL_STAGES } from './re/stages';
import { STATUS_LABEL as LOAN_STATUS_LABEL } from './LoanStatusPublicPage';
import { addressLine } from './shared/address';
import usePolling from './usePolling';
import { LINK_PAUSED_COPY } from './errors';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// The footer says the page updates itself; usePolling pauses while the tab is hidden.
const REFRESH_MS = 60000;

const TRACK = STAGES.filter((s) => !FAIL_STAGES.includes(s.key));

const FAIL_NOTICE = {
  cancelled: 'This transaction was cancelled',
  fell_through: 'This transaction fell through',
};

const titleCase = (v) => String(v).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const sideLabel = (side) => {
  if (!side) return null;
  if (side === 'buyer') return 'Buyer side';
  // The backend stores the seller side as "listing".
  if (side === 'seller' || side === 'listing') return 'Seller side';
  return titleCase(side);
};

const usd = (n) => Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const has = (v) => v !== null && v !== undefined && v !== '';

function errorMessage(e) {
  const code = e.response?.status;
  if (code === 403) return LINK_PAUSED_COPY; // workspace suspended (ticket 315)
  if (code === 404) return 'This transaction status link is invalid. Please request a new link from your agent.';
  if (code === 410) return 'This transaction status link has expired or been replaced. Please request a new link from your agent.';
  return 'Unable to load transaction status.';
}

export default function TransactionStatusPublicPage() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => axios.get(`${BACKEND}/api/re/transactions/status/${shareToken}`)
    .then((r) => setData(r.data))
    .catch((e) => {
      const status = e.response?.status;
      // A refresh that fails keeps the page the visitor already has; only a
      // link that is gone or paused (403, 404, 410) replaces it.
      if ([403, 404, 410].includes(status)) setErr(errorMessage(e));
      else if (!data) setErr(errorMessage(e));
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [shareToken]);
  usePolling(load, REFRESH_MS, { enabled: !err, immediate: false });

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#faf7f2' }}>
      <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#FEF3F2' }}>
          <Shield style={{ color: '#dc2626' }} />
        </div>
        <div className="font-serif text-lg font-bold text-brand-navy">Transaction Status Unavailable</div>
        <p className="text-slate-500 mt-2 text-sm">{err}</p>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf7f2' }}>
      <div className="text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>
    </div>
  );

  const prop = data.property || {};
  // The transaction's own address (what staff edit) wins; the linked listing's is the fallback.
  const address = addressLine(data.property_address) || addressLine(prop.address);
  const stageInfo = STAGES.find((s) => s.key === data.stage);
  const stageLabel = stageInfo?.label || (data.stage ? titleCase(data.stage) : null);
  const failNotice = FAIL_NOTICE[data.stage];
  const currentIdx = TRACK.findIndex((s) => s.key === data.stage);
  const sub = [sideLabel(data.side), data.contact_name].filter(Boolean).join(' · ');
  const loanStatus = data.linked_loan?.status;

  const details = [
    [has(data.contract_price) && 'Contract price', has(data.contract_price) && usd(data.contract_price), 'contract-price'],
    [data.contract_date && 'Contract date', data.contract_date && formatDate(data.contract_date), 'contract-date'],
    [has(data.option_period_days) && 'Option period', has(data.option_period_days) && `${data.option_period_days} days`, 'option-period'],
    [data.close_date && 'Estimated close date', data.close_date && formatDate(data.close_date), 'close-date'],
    [data.title_company && 'Title company', data.title_company, 'title-company'],
    [data.lender_name && 'Lender', data.lender_name, 'lender'],
    [loanStatus && 'Loan status', loanStatus && (LOAN_STATUS_LABEL[loanStatus] || titleCase(loanStatus)), 'loan-status'],
    [prop.mls_number && 'MLS #', prop.mls_number, 'mls-number'],
    [has(prop.list_price) && 'List price', has(prop.list_price) && usd(prop.list_price), 'list-price'],
  ].filter(([label, value]) => label && value);

  return (
    <div className="min-h-screen" style={{ background: '#faf7f2' }}>
      {/* Hero */}
      <div className="text-white" style={{ background: `linear-gradient(160deg, ${BRAND.navyDeep} 0%, ${BRAND.navy} 60%, ${BRAND.navyLight} 100%)` }}>
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand-accent-soft">
            <Shield size={12} /> Live Transaction Status
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold mt-2 break-words" data-testid="tx-public-heading">
            {address || 'Your transaction'}
          </h1>
          {sub && (
            <div className="text-white/70 mt-1 flex items-center gap-2 text-sm">
              <MapPin size={13} className="shrink-0" /> <span>{sub}</span>
            </div>
          )}
          {stageLabel && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded bg-brand-accent-soft text-brand-navy" data-testid="tx-public-stage-chip">{stageLabel}</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Progress */}
        {failNotice ? (
          <div className="md:col-span-3 rounded-2xl p-6 border flex items-center gap-3" style={{ background: '#FEF3F2', borderColor: '#fecaca' }} data-testid="tx-public-fail-notice">
            <XCircle size={22} className="shrink-0" style={{ color: '#dc2626' }} />
            <div className="font-serif font-semibold text-lg" style={{ color: '#b91c1c' }}>{failNotice}</div>
          </div>
        ) : (
          <div className="md:col-span-3 bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
            <div className="font-serif font-semibold text-lg mb-4 text-brand-navy">Progress</div>
            <div className="overflow-x-auto">
              <div className="flex items-start min-w-[600px]">
                {TRACK.map((s, i) => (
                  <div key={s.key} className="flex-1 relative flex flex-col items-center" data-testid={`stage-${s.key}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${i <= currentIdx ? 'text-white shadow' : 'bg-slate-100 text-slate-400'}`} style={i <= currentIdx ? { background: BRAND.accent } : {}}>
                      {i < currentIdx ? <CheckCircle2 size={16} /> : i === currentIdx ? <Clock size={16} /> : <Circle size={16} />}
                    </div>
                    {i < TRACK.length - 1 && (
                      <div className="absolute top-4 left-1/2 w-full h-0.5" style={{ background: i < currentIdx ? BRAND.accent : '#e2e8f0' }} />
                    )}
                    <div className={`text-[10px] uppercase tracking-wider mt-2 text-center px-1 ${i <= currentIdx ? 'font-bold' : 'text-slate-400'}`} style={i <= currentIdx ? { color: BRAND.navy } : {}}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Details */}
        {details.map(([label, value, key]) => (
          <DetailCard key={key} testid={key} label={label} value={value} />
        ))}

        <div className="md:col-span-3 text-center text-xs text-slate-400 pt-4">
          {data.updated_at && <div className="mb-1">Last updated {formatDate(data.updated_at)}</div>}
          This page updates automatically. Contact your agent with any questions.
          <div className="mt-1">Sensitive personal information is redacted from this public view.</div>
        </div>
      </div>
    </div>
  );
}

function DetailCard({ label, value, testid }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid={`tx-public-${testid}`}>
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-serif text-2xl font-bold mt-1 break-words" style={{ color: BRAND.navy }}>{value}</div>
    </div>
  );
}
